import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';
import { useNavigate } from 'react-router-dom';

const categoryIcons: Record<string, string> = {
  'Scarpe': 'ri-footprint-line',
  'Felpe/Maglioni': 'ri-t-shirt-line',
  'T-Shirt': 'ri-shirt-line',
  'Giacche': 'ri-t-shirt-air-line',
  'Pantaloni/Corti': 'ri-ruler-line',
  'Copricapi': 'ri-glasses-line',
  'Set': 'ri-stack-line',
  'Intimo': 'ri-heart-line',
  'Maglie Sportive': 'ri-basketball-line',
  'Accessori': 'ri-vip-diamond-line',
  'Borse': 'ri-shopping-bag-line',
  'Cinture': 'ri-link',
  'Profumi': 'ri-drop-line',
  'Altro': 'ri-more-line',
};

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 group cursor-pointer"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-leaf-line text-background-50 text-base md:text-lg"></i>
            </div>
            <span
              className={`font-heading text-lg md:text-xl font-bold whitespace-nowrap transition-colors ${
                scrolled ? 'text-foreground-900' : 'text-background-50'
              }`}
            >
              Itaobuy Sheet
            </span>
          </button>

          <div className="hidden md:flex items-center gap-6">
            <button onClick={() => navigate('/')} className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer flex items-center gap-1.5 ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'}`}>
              <i className="ri-home-line text-xs"></i>
              Home
            </button>
            <button onClick={() => document.getElementById('categories')?.scrollIntoView({ behavior: 'smooth' })} className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'}`}>
              Categorie
            </button>
            <button onClick={() => document.getElementById('guide')?.scrollIntoView({ behavior: 'smooth' })} className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'}`}>
              Guide
            </button>
            <button onClick={() => navigate('/articoli/itaobuy-spreadsheet-guida-completa')} className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer flex items-center gap-1.5 ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'}`}>
              <i className="ri-book-open-line text-xs"></i>
              Guida Acquisti
            </button>
            <button onClick={() => navigate('/articoli/itaobuy-spreadsheet-guida-completa')} className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'}`}>
              Blog
            </button>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  scrolled
                    ? 'bg-foreground-900 text-background-50 hover:bg-foreground-800'
                    : 'bg-background-50/20 backdrop-blur-sm text-background-50 hover:bg-background-50/30'
                }`}
              >
                Categorie
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-72 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 max-h-[420px] overflow-y-auto animate-fade-in z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-5 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-200/70 shrink-0">
                        <i className={`${categoryIcons[cat.nameIt] || 'ri-price-tag-3-line'} text-xs text-foreground-500`}></i>
                      </div>
                      <span className="font-medium">{cat.nameIt}</span>
                      <span className="text-foreground-400 ml-auto text-xs">({cat.name})</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`hidden sm:inline-flex items-center px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer border ${
                scrolled
                  ? 'border-foreground-300 text-foreground-700 hover:bg-foreground-50'
                  : 'border-background-50/40 text-background-50 hover:bg-background-50/20'
              }`}
            >
              itaobuy sheet
              <i className="ri-arrow-right-up-line ml-1.5 text-xs"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer bg-foreground-900 text-background-50 hover:bg-foreground-800"
            >
              buy it now
              <i className="ri-arrow-right-up-line ml-1.5 text-xs"></i>
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                scrolled ? 'text-foreground-700' : 'text-background-50'
              }`}
            >
              <i className={`text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 rounded-2xl shadow-lg border border-background-200/70 p-5 mb-3 animate-fade-in max-h-[70vh] overflow-y-auto">
            <div className="space-y-1 mb-4">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-xl transition-colors cursor-pointer hover:bg-background-100">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                  <i className="ri-home-line text-sm"></i>
                </div>
                <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">Home</span>
              </button>
              <button onClick={() => { document.getElementById('categories')?.scrollIntoView({ behavior: 'smooth' }); setMobileMenuOpen(false); }} className="flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-xl transition-colors cursor-pointer hover:bg-background-100">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                  <i className="ri-grid-line text-sm"></i>
                </div>
                <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">Categorie</span>
              </button>
              <button onClick={() => { document.getElementById('guide')?.scrollIntoView({ behavior: 'smooth' }); setMobileMenuOpen(false); }} className="flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-xl transition-colors cursor-pointer hover:bg-background-100">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-200 text-secondary-700 shrink-0">
                  <i className="ri-article-line text-sm"></i>
                </div>
                <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">Guide</span>
              </button>
              <button onClick={() => { navigate('/articoli/itaobuy-spreadsheet-guida-completa'); setMobileMenuOpen(false); }} className="flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-xl transition-colors cursor-pointer hover:bg-background-100">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                  <i className="ri-book-open-line text-sm"></i>
                </div>
                <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">Guida Acquisti</span>
              </button>
              <button onClick={() => { navigate('/articoli/itaobuy-spreadsheet-guida-completa'); setMobileMenuOpen(false); }} className="flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-xl transition-colors cursor-pointer hover:bg-background-100">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                  <i className="ri-newspaper-line text-sm"></i>
                </div>
                <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">Blog</span>
              </button>
            </div>

            <div className="border-t border-background-200/70 pt-4">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-foreground-400 px-3 mb-2">
                Tutte le Categorie
              </p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-3 py-2 text-xs rounded-lg transition-colors hover:bg-background-100 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <div className="w-6 h-6 flex items-center justify-center rounded-md bg-background-200/70 shrink-0">
                      <i className={`${categoryIcons[cat.nameIt] || 'ri-price-tag-3-line'} text-[10px] text-foreground-500`}></i>
                    </div>
                    <span className="font-medium text-foreground-700 whitespace-nowrap">{cat.nameIt}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200/70 pt-4 mt-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl bg-foreground-900 text-background-50 text-sm font-semibold whitespace-nowrap transition-all hover:bg-foreground-800 cursor-pointer"
              >
                buy it now
                <i className="ri-arrow-right-up-line text-xs"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl border border-foreground-200 text-foreground-700 text-sm font-medium whitespace-nowrap transition-all hover:bg-foreground-50 cursor-pointer mt-2"
              >
                itaobuy sheet
                <i className="ri-arrow-right-up-line text-xs"></i>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}